'use client'

import { useTranslations } from 'next-intl'
import { useState, useTransition } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Separator } from '@/components/ui/separator'
import { GithubIcon, GoogleIcon } from '@/components/auth/brand-icons'
import { EmailOtpForm } from '@/components/auth/email-otp-form'
import { PasswordForm } from '@/components/auth/password-form'
import { signInWithProviderAction } from '@/app/(auth)/sign-in/actions'

interface SignInFormProps {
	callbackUrl?: string
	oauth: { google: boolean; github: boolean }
}

export function SignInForm({ callbackUrl, oauth }: SignInFormProps) {
	const t = useTranslations('auth')
	const [isPending, startTransition] = useTransition()
	const [tab, setTab] = useState('email')
	const hasOAuth = oauth.google || oauth.github

	const signInWith = (provider: 'google' | 'github') => {
		startTransition(async () => {
			try {
				await signInWithProviderAction(provider, callbackUrl)
			} catch {
				toast.error(t('errors.generic'))
			}
		})
	}

	return (
		<div className="flex flex-col gap-6">
			{hasOAuth && (
				<div className="flex flex-col gap-2">
					<div className="grid gap-2 sm:grid-cols-2">
						{oauth.google && (
							<Button
								variant="outline"
								className="h-10"
								disabled={isPending}
								onClick={() => signInWith('google')}
							>
								<GoogleIcon />
								{t('oauth.google')}
							</Button>
						)}
						{oauth.github && (
							<Button
								variant="outline"
								className="h-10"
								disabled={isPending}
								onClick={() => signInWith('github')}
							>
								<GithubIcon />
								{t('oauth.github')}
							</Button>
						)}
					</div>
					<div className="flex items-center gap-3 py-1 text-xs text-muted-foreground uppercase">
						<Separator className="flex-1" />
						{t('oauth.or')}
						<Separator className="flex-1" />
					</div>
				</div>
			)}

			<Tabs value={tab} onValueChange={(value) => setTab(String(value))}>
				<TabsList className="w-full">
					<TabsTrigger value="email" className="flex-1">
						{t('tabs.email')}
					</TabsTrigger>
					<TabsTrigger value="username" className="flex-1">
						{t('tabs.username')}
					</TabsTrigger>
				</TabsList>
				<TabsContent value="email" className="pt-4">
					<EmailOtpForm callbackUrl={callbackUrl} />
				</TabsContent>
				<TabsContent value="username" className="pt-4">
					<PasswordForm callbackUrl={callbackUrl} />
				</TabsContent>
			</Tabs>
		</div>
	)
}
